import { useEffect, useState } from "react";
import "./MindMappingReportBFirstSection.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

const MindMappingReportBFirstSection = ({ res,toggle }) => {
  const [selectedAgeKey, setSelectedAgeKey] = useState("27-32");
  const [selectedGender, setSelectedGender] = useState("Female");

  useEffect(() => {
    // Standardize backend keys to match mapping keys
    if (res?.age) setSelectedAgeKey(res.age);
    if (res?.gender) setSelectedGender(res.gender);
  }, [res]);

  // Data for 1A - Founder Strategic Fit (Age Based)
  const tableDataAge = [
    { 
      age: "< 26", 
      strength: "Energy, curiosity, adaptability", 
      useCase: "Learn, experiment, fail fast", 
      startupTypes: "Tech MVPs, Apps, AI experiments, Content & Creator economy, Student startups", 
      preference: "⚠️ Low—Medium: Grants, student funds, micro angels, bootstrapping" 
    },
    { 
      age: "27-32", 
      strength: "Energy + early experience", 
      useCase: "🚀 High-growth startups", 
      startupTypes: "SaaS, Marketplaces, FinTech, EdTech, VC backed startups", 
      preference: "✅ High: Angels, Seed funds, Accelerators, Early-stage VCs" 
    },
    { 
      age: "33-42", 
      strength: "Domain expertise + credibility", 
      useCase: "🏆 Scalable & profitable startups", 
      startupTypes: "D2C SaaS, Enterprise solutions, Healthcare, Logistics, Manufacturing", 
      preference: "🔥 Very High: Seed > Series A/B VCs, Strategic Investors" 
    },
    { 
      age: "> 43", 
      strength: "Deep experience + strong networks", 
      useCase: "🎯 Niche, consulting, legacy ventures", 
      startupTypes: "Consulting, Advisory, Social impact, Family business, Franchising", 
      preference: "🧬 Selective: Angels, Strategic partners, PE (small), Self funded" 
    },
  ];

  // Data for 1B - Gender Dynamics (Based on provided Table 2 image)
  const genderMapping = {
    "Male": { 
      strengths: "Larger networks (historically), higher risk appetite, confident pitching style",
      challenges: "Overconfidence risk, higher failure rate in hyper-growth bets",
      perspective: "Neutral to slightly favorable (traditional VC bias still exists)",
      businessTypes: "High-growth startups, Tech, SaaS, Marketplaces, DeepTech"
    },
    "Female": { 
      strengths: "Strong execution discipline, capital efficiency, long-term focus, better team management",
      challenges: "Lower access to capital, bias in early pitching stages",
      perspective: "Increasingly preferred in many funds (diversity-driven + performance data)",
      businessTypes: "D2C, HealthTech, EdTech, FinTech, Impact & Sustainable businesses"
    },
    "Other": { 
      strengths: "Unique perspectives, inclusivity-driven innovation, strong community alignment",
      challenges: "Bias, limited access to networks & capital, underrepresentation",
      perspective: "Progressive funds supportive; mainstream VCs still limited",
      businessTypes: "Social impact, community platforms, creative tech, niche B2C"
    },
    "Third Gender": { 
      strengths: "Unique perspectives, inclusivity-driven innovation, strong community alignment",
      challenges: "Bias, limited access to networks & capital, underrepresentation",
      perspective: "Progressive funds supportive; mainstream VCs still limited",
      businessTypes: "Social impact, community platforms, creative tech, niche B2C"
    }
  };

  const activeAgeRow = tableDataAge.find(item => item.age === selectedAgeKey) || tableDataAge[1];
  const activeGenderRow = genderMapping[selectedGender] || genderMapping["Female"];

  return (
    <div className="mind-mapping-report-b-first-container">
      <div className={toggle?"table-adjust":"mind-mapping-report-b-first-final-table"}> 
        {/* Decorative Corners */}
        <span className="mind-mapping-report-b-first-corner mind-mapping-report-b-first-corner-top-left"></span>
        <span className="mind-mapping-report-b-first-corner mind-mapping-report-b-first-corner-top-right"></span>
        <span className="mind-mapping-report-b-first-corner mind-mapping-report-b-first-corner-bottom-left"></span>
        <span className="mind-mapping-report-b-first-corner mind-mapping-report-b-first-corner-bottom-right"></span>

        <div className="mind-mapping-report-b-first-module-title">
           <span className="module-prefix">Module 1:</span> Founder Age & Gender vs Strategic Fit
        </div>

        {/* SECTION 1A: Founder Age Fit */}
        <QTag q="How old are you?" a={`${activeAgeRow.age} years`} impacts="Startup type preference, risk appetite, investor appeal, and growth strategy" />
        <div className="mind-mapping-report-b-first-table-scroll-wrapper">
          <table className="mind-mapping-report-b-first-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-b-first-col-sno">S.NO</th>
                <th>Age Group</th>
                <th>Primary Strength</th>
                <th>Best Use Case</th>
                <th>Business Types</th>
                <th>Investor Preference</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="mind-mapping-report-b-first-col-sno">1 A</td>
                <td className="mind-mapping-report-b-first-user-val">{activeAgeRow.age}</td>
                <td>{activeAgeRow.strength}</td>
                <td className="mind-mapping-report-b-first-highlight">{activeAgeRow.useCase}</td>
                <td>{activeAgeRow.startupTypes}</td>
                <td>{activeAgeRow.preference}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 1B: Founder Gender Dynamics */}
        <QTag q="What is your gender?" a={selectedGender} impacts="Capital access pathways, investor bias awareness, network dynamics, and best-fit sectors" />
        <div className="mind-mapping-report-b-first-table-scroll-wrapper" style={{ marginTop: "10px" }}>
          <table className="mind-mapping-report-b-first-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-b-first-col-sno">S.NO</th>
                <th>Gender</th>
                <th>Key Strengths / Advantages</th>
                <th>Common Challenges / Disadvantages</th>
                <th>Investor Perspective (Reality)</th>
                <th>Best-Fit Business Types (Typical)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="mind-mapping-report-b-first-col-sno">1 B</td>
                <td className="mind-mapping-report-b-first-user-val">{selectedGender}</td>
                <td>{activeGenderRow.strengths}</td>
                <td>{activeGenderRow.challenges}</td>
                <td>{activeGenderRow.perspective}</td>
                <td>{activeGenderRow.businessTypes}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBFirstSection;